import { useCallback } from 'react'

import { ClaimFormValues } from '~/components/smart-accounts/vesting/claimSchema'
import { VestingBalance } from '~/components/smart-accounts/vesting/types'
import {
  buildVestingClaimData,
  buildVestingClaimName,
} from '~/components/smart-accounts/vesting/utils'
import { CreateTransactionData } from '~/hooks/smart-account-actions/createTransactionData.type'
import { useCreateTransaction } from '~/hooks/smart-account-actions/useCreateTransaction'
import { TokenMetadata } from '~/hooks/useTokenMetadata'
import {
  BackendLoggingData,
  BasicTransactionType,
  OrgTransaction,
} from '~/types'

type CreateClaimTransactionParams = {
  claimForm: ClaimFormValues
  tokenMetadata: TokenMetadata
  vestingBalance: VestingBalance
  loggingData?: BackendLoggingData
}

export const useCreateClaimTransaction = (): {
  createClaimTransaction: (
    data: CreateClaimTransactionParams,
    dontOpenDialog: boolean,
    onSuccess?: (transaction: OrgTransaction) => void,
  ) => Promise<void>
  isProcessing: boolean
} => {
  const { createTransaction, isProcessing } = useCreateTransaction()

  const createClaimTransaction = useCallback(
    async (
      data: CreateClaimTransactionParams,
      dontOpenDialog: boolean,
      onSuccess?: (transaction: OrgTransaction) => void,
    ) => {
      const createTransactionData: CreateTransactionData = {
        chainId: data.claimForm.chainId,
        name: buildVestingClaimName({
          vestingBalance: data.vestingBalance,
          tokenMetadata: data.tokenMetadata,
        }),
        smartAccountId: data.vestingBalance.smartAccountId,
        smartAccountAddress: data.claimForm.account,
        calls: data.claimForm.calls,
        isOfframp: false,
        onSuccess,
        memo: data.claimForm.memo,
        types: [BasicTransactionType.Claim],
        transactionData: {
          claimData: buildVestingClaimData({
            vestingBalance: data.vestingBalance,
            chainId: data.claimForm.chainId,
            tokenMetadata: data.tokenMetadata,
          }),
        },
      }

      createTransaction({
        transactionId: undefined,
        txData: createTransactionData,
        dontOpenDialog,
        loggingData: data.loggingData,
      })
    },
    [createTransaction],
  )

  return {
    createClaimTransaction,
    isProcessing,
  }
}
